/**
 * Map a click's caret point to a ProseMirror position within a painted
 * paragraph (MDRS-292).
 *
 * The docx editor renders PM off-screen and paints the visible pages
 * separately, so the browser's `caretRangeFromPoint` / `caretPositionFromPoint`
 * gives a DOM `(node, offset)` in the PAINTED layer — not a PM position. The
 * visible `.layout-paragraph` fragments carry `data-pm-start` (the paragraph's
 * PM node position), so we map the caret the same way Refine and the spellcheck
 * overlay do: `pmStart + 1 + charOffset`, where `charOffset` is the plain-text
 * offset of the caret within the paragraph.
 *
 * This is what lets dictation insert at the caret (where the user clicked)
 * instead of at the end of the paragraph.
 */

/**
 * Character offset of a caret point `(node, offset)` within `paraEl` — the sum
 * of the text of every text node before `node` in document order, plus `offset`.
 * Marks (bold/italic spans) add no PM positions for plain text, so the flattened
 * text offset equals the PM content offset. Falls back to the running total when
 * the point isn't a text node under `paraEl`.
 *
 * Caveat: this assumes the paragraph is text + marks only. Inline atom nodes
 * (hard breaks, images, MDRS-91 mic-anchor decorations) each occupy one PM
 * position but contribute zero/one DOM chars, so a paragraph containing them
 * can map off by the atom count. `caretPmPosWithinParagraph` clamps to the
 * paragraph bounds, so the worst case is an in-bounds mis-position that falls
 * back toward the end — never an out-of-range insert. Matches the existing
 * Refine/spellcheck `pmStart + 1 + charOffset` convention.
 */
export function caretOffsetWithinParagraph(
  paraEl: Element,
  node: Node,
  offset: number
): number {
  const walker = document.createTreeWalker(paraEl, NodeFilter.SHOW_TEXT);
  let total = 0;
  let cur: Node | null = walker.nextNode();
  while (cur) {
    if (cur === node) return total + offset;
    total += (cur.nodeValue ?? "").length;
    cur = walker.nextNode();
  }
  // The caret landed on an element boundary (not a text node) under paraEl —
  // best-effort: clamp to the end of the walked text.
  return total;
}

/**
 * PM position for a caret point within `paraEl`: `pmStart + 1 + charOffset`,
 * clamped to the paragraph's inline content `[pmStart + 1, pmEnd - 1]`. Returns
 * null when the paragraph's `data-pm-*` attrs are missing/unparseable, so the
 * caller can fall back to its previous behaviour.
 */
export function caretPmPosWithinParagraph(
  paraEl: Element,
  node: Node,
  offset: number
): number | null {
  const el = paraEl as HTMLElement;
  const pmStart = Number(el.dataset?.pmStart);
  const pmEnd = Number(el.dataset?.pmEnd);
  if (Number.isNaN(pmStart) || Number.isNaN(pmEnd)) return null;
  const charOffset = caretOffsetWithinParagraph(paraEl, node, offset);
  const pos = pmStart + 1 + charOffset;
  // Keep the caret inside the paragraph's inline content: never before the
  // opening token (pmStart) or after the closing one (pmEnd).
  const lo = pmStart + 1;
  const hi = Math.max(lo, pmEnd - 1);
  return Math.min(Math.max(pos, lo), hi);
}
